import { useEffect, useState } from 'react';
import { welcome } from '@magic/data/messages';
import { Button, IconButton } from '@resultadosdigitais/tangram-components';
import { Times } from '@resultadosdigitais/tangram-react-icons';
import { differenceInHours } from 'date-fns/differenceInHours';
import styled, { keyframes } from 'styled-components';

import { useAgent } from '../context/agent';
import { useUI } from '../context/ui';
import { useLocalStorage } from '../hooks/use-local-storage';
import { Loader } from './loader';

export const baloonAnimation = keyframes`
  0% {width: 0;}
  10% {width: 120px;}
  20%, 50% {width: 100px;}
  60% {width: 330px;}
  75%, 100% {width: min(calc(100vw - 116px), 315px)}
`;

export const BalloonContainer = styled.div`
  width: 0;
  cursor: pointer;
  height: 70px;
  background-color: #ffffff;
  box-shadow: 5px 0px 21px 0px rgba(0, 0, 0, 0.15);
  text-align: left;
  display: flex;
  justify-content: center;
  align-items: center;
  animation-name: ${baloonAnimation};
  animation-delay: 1s;
  animation-duration: 3.5s;
  animation-fill-mode: forwards;
`;

export const fadeInAnimation = keyframes`
  0%   {opacity: 0;}
  100% {opacity: 1;}
`;

export const BalloonText = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  padding: 0 var(--size-spacing-04);
  animation-name: ${fadeInAnimation};
  animation-delay: 3.5s;
  animation-duration: 0.3s;
  animation-fill-mode: forwards;
  cursor: pointer;
`;

const fadeInOutAnimation = keyframes`
  0%   {opacity: 0;}
  15%   {opacity: 1;}
  100% {opacity: 0;}
`;

export const BalloonTyping = styled.div`
  padding: 10px 95px;
  position: absolute;
  opacity: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  animation-name: ${fadeInOutAnimation};
  animation-delay: 1.5s;
  animation-duration: 2s;
  animation-fill-mode: forwards;
`;

const BalloonClose = styled(IconButton)<{ left: boolean }>`
  position: absolute;
  top: -12px;
  ${(props) => (props.left ? 'right: -16px;' : 'left: -16px;')}
  background: var(--neutral-surface-low-emphasis);
  margin: 0;
  height: 32px;
  width: 32px;
  padding: 0;
  border-radius: 50%;

  > svg {
    opacity: 0.6;
  }
`;

export const Balloon = () => {
  const agent = useAgent();
  const { ui } = useUI();
  const [showMessage, setShowMessage] = useState(false);
  const [expiry, setExpiry] = useLocalStorage<Date>(
    `balloon_${agent.id}`,
    undefined!
  );

  useEffect(() => {
    if (!expiry || differenceInHours(new Date(), new Date(expiry)) > 24) {
      setShowMessage(true);
      setExpiry(new Date());
    }
  }, [expiry]);

  return ui.open || !showMessage ? null : (
    <BalloonContainer>
      <BalloonTyping>
        <Loader />
      </BalloonTyping>

      <BalloonText>
        <BalloonClose
          kind={Button.kinds.tertiary}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setShowMessage(false);
          }}
          left={agent.left}
        >
          <Times size={Times.sizes.sm} title="Close" />
        </BalloonClose>
        {agent?.welcome || welcome}
      </BalloonText>
    </BalloonContainer>
  );
};
